import { CycleAnalysisParams, CycleAnalysisResult, CycleAnalysisService } from '@/services/cycle-analysis.service'
import { computed, ref } from 'vue'

export interface CycleAnalysisConfig {
  window_period: string
  min_cycle_duration: number
  max_cycle_duration: number
  change_percentile: number
  min_data_points: number
  smoothing_factor: number
  use_neighbor_average: boolean
  rate_multiplier: number
}

export function useCycleAnalysis() {
  const isAnalyzing = ref(false)
  const analysisResult = ref<CycleAnalysisResult | null>(null)
  const error = ref<string | null>(null)

  // Configurações padrão
  const config = ref<CycleAnalysisConfig>({
    window_period: '1m',
    min_cycle_duration: 20,
    max_cycle_duration: 350,
    change_percentile: 70,
    min_data_points: 10,
    smoothing_factor: 1.0,
    use_neighbor_average: true,
    rate_multiplier: 2.0,
  })

  // Computed para estatísticas
  const cycleCount = computed(() => analysisResult.value?.cycle_count || 0)
  const totalTime = computed(() => analysisResult.value?.total_time || 0)
  const averageTime = computed(() => analysisResult.value?.average_time || 0)
  const periods = computed(() => analysisResult.value?.periods || [])

  const runAnalysis = async (sensorData: any, startDate: Date, endDate: Date) => {
    isAnalyzing.value = true
    error.value = null

    try {
      const params: CycleAnalysisParams = {
        sensor: sensorData,
        start_date: startDate,
        end_date: endDate,
        ...config.value,
      }

      const service = new CycleAnalysisService(params)
      const result = await service.call()

      analysisResult.value = result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao executar análise de ciclos'
    } finally {
      isAnalyzing.value = false
    }
  }

  const clearResults = () => {
    analysisResult.value = null
    error.value = null
  }

  const updateConfig = (newConfig: Partial<CycleAnalysisConfig>) => {
    config.value = { ...config.value, ...newConfig }
  }

  return {
    // Estado
    isAnalyzing,
    analysisResult,
    error,
    config,

    // Computed
    cycleCount,
    totalTime,
    averageTime,
    periods,

    // Métodos
    runAnalysis,
    clearResults,
    updateConfig,
  }
}
